import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import {
  ADHOC_INVOICE_TYPES,
  BILL_PAY_METHODS,
  CARD_CODES,
  DISCOUNT_APPLICABLE_TYPES,
  DISCOUNT_CREDIT_OPTIONS,
  EBILL_OPTIONS,
  REFUND_TYPES,
  refundSchema,
} from '@app/shared';
import type {
  BillPayMethod,
  CardPaymentInput,
  CustomerBillingDto,
  DiscountCreditLogDto,
  EBillOption,
  InvoiceDto,
  Paged,
  PaymentRowDto,
  RecurringDiscountDto,
  RefundInput,
  SettingsRecordDto,
  WalletLogDto,
} from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { StatusChip } from '../../components/StatusChip';
import { FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { CardField, useCardsAvailable } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';
import {
  ApplyCouponPanel,
  AutopayLogList,
  DeclinedPaymentList,
  OcsPurchaseList,
  TaxExemptionDialog,
} from './BillingExtras';
import { useCustomerContext } from './CustomerLayout';
import { CouponsLog } from './MoneyPages';
import { Section, userLabel } from './parts';

type Problems = Record<string, string>;
const fromServer = (err: unknown): Problems =>
  err instanceof ApiError ? Object.fromEntries(err.details.map((d) => [d.path, d.message])) : {};

/** "12.50" or "-5" to cents; NaN when it is not an amount. */
const toCents = (text: string, signed = false): number => {
  const t = text.trim();
  if (!(signed ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/).test(t)) return Number.NaN;
  return Math.round(Number(t) * 100);
};
const READ_ONLY = { input: { readOnly: true } } as const;
const billingKey = (customerId: string) => ['billing', 'customer', customerId] as const;

/* ------------------------------- The lists -------------------------------- */

type ListKey =
  | 'invoices'
  | 'payments'
  | 'declined'
  | 'credit'
  | 'wallet'
  | 'recurring'
  | 'coupons'
  | 'ocs'
  | 'autopay'
  | 'applyCoupon';
const LIST_TITLES: Record<ListKey, string> = {
  invoices: 'Regular Invoices (PC72)',
  payments: 'Payments (PC463)',
  credit: 'Discount Credit Log (843)',
  wallet: 'Wallet Log (PC466)',
  recurring: 'Recurring Discount Log (843)',
  coupons: 'Coupons log (PC1195)',
  declined: 'Declined Payment (PC932)',
  ocs: 'OCS Purchase (PC465)',
  autopay: 'Autopay Log (PC678)',
  applyCoupon: 'Apply Coupon (PC1195)',
};
type DialogKey = 'pay' | 'adhoc' | 'credit' | 'addWallet' | 'adjustWallet' | 'ebill' | 'tax';
type DiscountOption = (typeof DISCOUNT_CREDIT_OPTIONS)[number];
type DiscountApplicable = (typeof DISCOUNT_APPLICABLE_TYPES)[number];

/** A label and a value of an expanded row. */
const Pair = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ display: 'contents' }}>
    <Typography component="dt" color="text.secondary" sx={{ fontSize: 13 }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 13 }}>
      {value}
    </Typography>
  </Box>
);
const Block = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box>
    <Typography variant="subtitle2" component="h4" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Box
      component="dl"
      sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.25 }}
    >
      {children}
    </Box>
  </Box>
);

/**
 * "Regular Invoices" (PC72): the reference's columns, a row that opens on its breakdown, and
 * "Void" (PC609) on an invoice nothing has been paid on.
 */
function InvoiceList({
  invoices,
  onVoid,
  canVoid,
  busy,
  onRefund,
}: {
  invoices: InvoiceDto[];
  onVoid: (invoice: InvoiceDto) => void;
  canVoid: boolean;
  busy: boolean;
  /** "Refund" (PC692, switch payment-refunds); absent when the user may not refund. */
  onRefund?: (invoice: InvoiceDto) => void;
}) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All Statuses');
  const [open, setOpen] = useState<string | null>(null);
  const needle = search.trim().toLowerCase();
  const rows = invoices.filter(
    (i) =>
      (status === 'All Statuses' || i.status === status) &&
      (!needle ||
        i.invoiceNo.toLowerCase().includes(needle) ||
        i.invoiceType.toLowerCase().includes(needle)),
  );
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <TextField
          label="Search"
          placeholder="Invoice #, type..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth={false}
          sx={{ width: 240 }}
        />
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          fullWidth={false}
          sx={{ width: 180 }}
        >
          {['All Statuses', 'Paid', 'Unpaid'].map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
        Expand for Debit(A), Tax(B), Processing(C), Created Date &amp; Created By.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Regular Invoices">
          <TableHead>
            <TableRow>
              <TableCell width={40} />
              <TableCell>Invoice No.</TableCell>
              <TableCell>Invoice Type</TableCell>
              <TableCell>Paid / Unpaid Status</TableCell>
              <TableCell align="right">Inv Balance</TableCell>
              <TableCell align="right">Amount Paid($)</TableCell>
              <TableCell align="right">Total Due($)</TableCell>
              <TableCell>Action (PC609)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>No record found</TableCell>
              </TableRow>
            ) : null}
            {rows.map((i) => (
              <Fragment key={i.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      aria-label={`Show full breakdown of ${i.invoiceNo}`}
                      onClick={() => setOpen(open === i.id ? null : i.id)}
                    >
                      {open === i.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {i.invoiceNo}
                    <Typography sx={{ fontSize: 12 }} color="text.secondary">
                      Generated {formatDate(i.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>{i.invoiceType}</TableCell>
                  <TableCell>
                    <StatusChip status={i.status} />
                    {i.status === 'Paid' ? (
                      <Typography sx={{ fontSize: 12 }}>
                        {i.paidMethod} {formatDate(i.paidAt)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">{formatCents(i.balanceCents)}</TableCell>
                  <TableCell align="right">{formatCents(i.amountPaidCents)}</TableCell>
                  <TableCell align="right">
                    {formatCents(i.totalDueCents)}
                    <Typography sx={{ fontSize: 12 }} color="text.secondary">
                      {formatDate(i.dueDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {canVoid && i.status === 'Unpaid' && i.amountPaidCents === 0 ? (
                      <Button size="small" color="error" disabled={busy} onClick={() => onVoid(i)}>
                        Void
                      </Button>
                    ) : null}
                    {onRefund && i.refundable ? (
                      <Button size="small" onClick={() => onRefund(i)}>
                        Refund
                      </Button>
                    ) : null}
                    {i.refundedCents > 0 ? (
                      <Typography sx={{ fontSize: 12 }} color="text.secondary">
                        Refunded {formatCents(i.refundedCents)}
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
                {open === i.id ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: 'action.hover' }}>
                      <Box
                        aria-label={`Breakdown of ${i.invoiceNo}`}
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        }}
                      >
                        <Block title="Amounts">
                          <Pair label="Debit (A)" value={formatCents(i.debitCents)} />
                          <Pair label="Tax (B)" value={formatCents(i.taxCents)} />
                          <Pair
                            label="Processing Fee (C)"
                            value={formatCents(i.processingFeeCents)}
                          />
                          <Pair label="Inv Balance D={A+B+C}" value={formatCents(i.balanceCents)} />
                          <Pair label="Amount Paid (E)" value={formatCents(i.amountPaidCents)} />
                          <Pair label="Total Due (D−E)" value={formatCents(i.totalDueCents)} />
                        </Block>
                        <Block title="Invoice">
                          <Pair label="Invoice type" value={i.invoiceType} />
                          <Pair label="Description" value={i.description} />
                          <Pair label="From date" value={formatDate(i.date)} />
                        </Block>
                        <Block title="Payment & audit">
                          <Pair
                            label="Paid / unpaid"
                            value={`${i.status}${i.paidMethod ? ` ${i.paidMethod}` : ''}`}
                          />
                          <Pair label="Source" value={i.source} />
                          <Pair label="Created Date" value={formatDateTime(i.date)} />
                          <Pair label="Created By" value={userLabel(i.createdBy)} />
                        </Block>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

/** "Payments" (PC463), with the reference's "Payment breakdown" behind each row. */
function PaymentList({ payments }: { payments: PaymentRowDto[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Payments">
        <TableHead>
          <TableRow>
            <TableCell width={40} />
            <TableCell>Invoice No.</TableCell>
            <TableCell>Invoice Type</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="right">Tax</TableCell>
            <TableCell align="right">Payment Amount</TableCell>
            <TableCell>Payment Type</TableCell>
            <TableCell>Payment By</TableCell>
            <TableCell>Transaction ID</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9}>No record found</TableCell>
            </TableRow>
          ) : null}
          {payments.map((p) => (
            <Fragment key={p.id}>
              <TableRow hover>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={`Show full payment details of ${p.invoiceNo}`}
                    onClick={() => setOpen(open === p.id ? null : p.id)}
                  >
                    {open === p.id ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                  </IconButton>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {p.invoiceNo}
                  <Typography sx={{ fontSize: 12 }} color="text.secondary">
                    {formatDate(p.date)}
                  </Typography>
                </TableCell>
                <TableCell>{p.invoiceType}</TableCell>
                <TableCell align="right">{formatCents(p.amountCents)}</TableCell>
                <TableCell align="right">{formatCents(p.taxCents)}</TableCell>
                <TableCell align="right">{formatCents(p.paymentCents)}</TableCell>
                <TableCell>{p.method}</TableCell>
                <TableCell>
                  {userLabel(p.paymentBy)}
                  <Typography sx={{ fontSize: 12 }} color="text.secondary">
                    {p.source}
                  </Typography>
                </TableCell>
                <TableCell>{p.transactionId}</TableCell>
              </TableRow>
              {open === p.id ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ bgcolor: 'action.hover' }}>
                    <Typography sx={{ fontSize: 13, mb: 1 }}>
                      Payment breakdown — Full amount and audit details for this credit transaction.
                    </Typography>
                    <Box
                      aria-label={`Payment breakdown of ${p.invoiceNo}`}
                      sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      }}
                    >
                      <Block title="Amounts">
                        <Pair
                          label="Principal / invoice amount"
                          value={formatCents(p.amountCents)}
                        />
                        <Pair label="Tax" value={formatCents(p.taxCents)} />
                        <Pair label="Processing fee" value={formatCents(p.processingFeeCents)} />
                        <Pair label="Payment amount (CR)" value={formatCents(p.paymentCents)} />
                      </Block>
                      <Block title="Invoice">
                        <Pair label="Invoice number" value={p.invoiceNo} />
                        <Pair label="Invoice type" value={p.invoiceType} />
                        <Pair label="Description" value={p.description} />
                        <Pair label="Payment mode" value={p.paymentMode} />
                      </Block>
                      <Block title="Payment & audit">
                        <Pair label="Payment method" value={p.method} />
                        <Pair label="Payment by" value={userLabel(p.paymentBy)} />
                        <Pair label="Source" value={p.source} />
                        <Pair label="Transaction ID" value={p.transactionId} />
                      </Block>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const WALLET_COLUMNS: Column<WalletLogDto>[] = [
  { key: '#', header: '#', width: 50, render: (_t, i) => i + 1 },
  { key: 'amount', header: 'Amount', align: 'right', render: (t) => formatCents(t.amountCents) },
  { key: 'at', header: 'Date', nowrap: true, render: (t) => formatDateTime(t.at) },
  { key: 'type', header: 'Type', render: (t) => t.type },
  { key: 'purchaseType', header: 'Purchase Type', render: (t) => t.purchaseType },
  { key: 'transactionId', header: 'Transaction ID', render: (t) => t.transactionId },
  { key: 'invoiceNo', header: 'Invoice Number', nowrap: true, render: (t) => t.invoiceNo },
  { key: 'by', header: 'Created By', render: (t) => userLabel(t.by) },
  { key: 'source', header: 'Source', render: (t) => t.source },
];

// The reference's columns of these two logs were not seen (no record on the account read);
// these are this application's.
const CREDIT_COLUMNS: Column<DiscountCreditLogDto>[] = [
  { key: '#', header: '#', width: 50, render: (_t, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (t) => formatDateTime(t.at) },
  { key: 'option', header: 'Discount Credit Option', render: (t) => t.option },
  { key: 'applicable', header: 'Applicable Type', render: (t) => t.applicableType },
  { key: 'type', header: 'Discount Credit Type', render: (t) => t.type },
  { key: 'amount', header: 'Amount', align: 'right', render: (t) => formatCents(t.amountCents) },
  {
    key: 'balance',
    header: 'Balance',
    align: 'right',
    render: (t) => formatCents(t.balanceAfterCents),
  },
  { key: 'months', header: 'Months', align: 'right', render: (t) => (t.months ? t.months : '') },
  { key: 'invoiceNo', header: 'Invoice Number', nowrap: true, render: (t) => t.invoiceNo },
  { key: 'by', header: 'Created By', render: (t) => userLabel(t.by) },
];

const RECURRING_COLUMNS: Column<RecurringDiscountDto>[] = [
  { key: '#', header: '#', width: 50, render: (_d, i) => i + 1 },
  { key: 'at', header: 'Date', nowrap: true, render: (d) => formatDateTime(d.at) },
  { key: 'type', header: 'Discount Credit Type', render: (d) => d.type },
  { key: 'amount', header: 'Amount', align: 'right', render: (d) => formatCents(d.amountCents) },
  { key: 'months', header: 'Recurring Month Count', align: 'right', render: (d) => d.months },
  { key: 'remaining', header: 'Months Left', align: 'right', render: (d) => d.remaining },
  { key: 'status', header: 'Status', render: (d) => <StatusChip status={d.status} /> },
  { key: 'by', header: 'Created By', render: (d) => userLabel(d.by) },
];

/* -------------------------------- The dialogs ------------------------------ */

/** The customer, as the top of the reference's dialogs shows it. */
function WhoBlock() {
  const { customer } = useCustomerContext();
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, mb: 1 }}>
      <Typography color="text.secondary">Customer ID</Typography>
      <Typography>{customer.customerId}</Typography>
      <Typography color="text.secondary">Customer Name</Typography>
      <Typography>
        {customer.firstName} {customer.lastName}
      </Typography>
      <Typography color="text.secondary">Phone Number</Typography>
      <Typography>{customer.line.mdn ?? ''}</Typography>
    </Box>
  );
}

function BillingDialogs({
  open,
  billing,
  onClose,
}: {
  open: DialogKey | null;
  billing: CustomerBillingDto;
  onClose: () => void;
}) {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const base = `/billing/customers/${customer.id}`;
  const [problems, setProblems] = useState<Problems>({});
  // Each dialog keeps its own fields; they start over when a dialog opens.
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<BillPayMethod>('cash');
  const [receipt, setReceipt] = useState('');
  const [mode, setMode] = useState<'cash' | 'skip' | 'card'>('cash');
  const cardsAvailable = useCardsAvailable();
  const cardRef = useRef<CardFieldHandle>(null);
  /** The card typed in or picked, or a problem shown under the card field. */
  const collectCard = async (): Promise<CardPaymentInput | null> => {
    try {
      return (await cardRef.current?.collect()) ?? null;
    } catch (err) {
      setProblems({ card: (err as Error).message });
      return null;
    }
  };
  const [reason, setReason] = useState('');
  const [invoiceType, setInvoiceType] = useState('');
  const [includeTax, setIncludeTax] = useState(true);
  const [description, setDescription] = useState('');
  const [eBill, setEBill] = useState<EBillOption>(billing.eBill);
  const [creditOption, setCreditOption] = useState<DiscountOption | ''>('');
  const [applicable, setApplicable] = useState<DiscountApplicable>('One Time');
  const [months, setMonths] = useState('');
  const [typeId, setTypeId] = useState('');
  const [shown, setShown] = useState<DialogKey | null>(null);
  if (open !== shown) {
    setShown(open);
    setProblems({});
    setAmount(open === 'pay' ? (billing.payDefaultCents / 100).toFixed(2) : '');
    setMethod(BILL_PAY_METHODS.find((m) => can(m.code) && m.key !== 'card')?.key ?? 'cash');
    setReceipt('');
    setMode('cash');
    setReason('');
    setInvoiceType('');
    setIncludeTax(true);
    setDescription('');
    setEBill(billing.eBill);
    setCreditOption('');
    setApplicable('One Time');
    setMonths('');
    setTypeId('');
  }
  // "Choose Discount Credit Type": Manage Discount Credit Types (PC843).
  const creditTypes = useQuery({
    queryKey: ['settings-lists', 'discount-credit-types', 'options'],
    queryFn: () =>
      api.get<Paged<SettingsRecordDto>>('/settings-lists/discount-credit-types', {
        pageSize: 200,
        sort: 'number',
      }),
    select: (r) => r.data,
    enabled: open === 'credit',
  });

  const send = useMutation({
    mutationFn: ({ path, body, put }: { path: string; body: unknown; put?: boolean }) =>
      put
        ? api.put<CustomerBillingDto>(`${base}/${path}`, body)
        : api.post<CustomerBillingDto>(`${base}/${path}`, body),
    onSuccess: (next) => {
      queryClient.setQueryData(billingKey(customer.id), next);
      // The profile shows the wallet balance too.
      return queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const run = async (path: string, body: unknown, done: string, put = false) => {
    try {
      await send.mutateAsync({ path, body, put });
      notify.success(done);
      onClose();
    } catch (err) {
      const found = fromServer(err);
      if (Object.keys(found).length) setProblems(found);
      else notify.error(err);
    }
  };
  const cents = (signed = false) => toCents(amount, signed);
  const needAmount = (signed = false): boolean => {
    const value = cents(signed);
    if (Number.isNaN(value) || value === 0) {
      setProblems({ amountCents: signed ? 'Adjust Amount is required' : 'Amount is required' });
      return false;
    }
    return true;
  };
  const amountField = (label: string, signed = false) => (
    <TextField
      required
      label={label}
      value={amount}
      onChange={(e) => setAmount(e.target.value)}
      error={Boolean(problems.amountCents)}
      helperText={problems.amountCents || (signed ? 'A minus takes money off' : undefined)}
      slotProps={{ htmlInput: { maxLength: 9, inputMode: 'decimal' } }}
    />
  );
  const submit =
    (handler: () => void | Promise<void>) =>
    (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      void handler();
    };

  const total = Number.isNaN(cents()) ? 0 : cents();
  return (
    <>
      <FormDialog
        open={open === 'pay'}
        title="Pay Invoice(s)"
        onClose={onClose}
        submitting={send.isPending}
        onSubmit={submit(async () => {
          if (!needAmount()) return;
          if (method === 'card') {
            const card = await collectCard();
            if (!card) return;
            return run('pay-invoices', { amountCents: cents(), method, card }, 'Payment received.');
          }
          if (method !== 'wallet' && !receipt.trim())
            return setProblems({
              receiptNumber:
                method === 'cheque' ? 'Cheque Number is required' : 'Receipt Number is required',
            });
          return run(
            'pay-invoices',
            { amountCents: cents(), method, receiptNumber: receipt },
            'Payment received.',
          );
        })}
      >
        <Alert severity="info" sx={{ mb: 2 }}>
          Note: 1. If there is no pending outstanding invoice on this account hence default payable
          amount would be similar to customer plan amount. 2. Any additional amount greater than
          total due amount will be added to Customer wallet instantly
        </Alert>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Customer ID" value={customer.customerId} disabled />
          {amountField('Amount (A) ($)')}
          <TextField label="Processing Fee (B) ($)" value="0.00" slotProps={READ_ONLY} />
          <TextField
            label="Total Amount (A+B) ($)"
            value={(total / 100).toFixed(2)}
            slotProps={READ_ONLY}
          />
          <Box>
            <FormLabel id="pay-type">Payment Type</FormLabel>
            <RadioGroup
              row
              aria-labelledby="pay-type"
              value={method}
              onChange={(e) => setMethod(e.target.value as BillPayMethod)}
            >
              {BILL_PAY_METHODS.filter(
                (m) => can(m.code) && (m.key !== 'card' || cardsAvailable),
              ).map((m) => (
                <FormControlLabel
                  key={m.key}
                  value={m.key}
                  control={<Radio />}
                  label={
                    m.key === 'wallet'
                      ? `Wallet Balance (${formatCents(billing.walletBalanceCents)}) (${m.code})`
                      : `${m.label} (${m.code})`
                  }
                />
              ))}
            </RadioGroup>
            {problems.method ? <Alert severity="error">{problems.method}</Alert> : null}
          </Box>
          {method === 'card' ? (
            <CardField
              ref={cardRef}
              customerId={customer.id}
              error={problems.card ?? problems['card.savedCardId']}
            />
          ) : null}
          {method !== 'wallet' && method !== 'card' ? (
            <TextField
              required
              label={method === 'cheque' ? 'Cheque Number' : 'Receipt Number'}
              value={receipt}
              onChange={(e) => setReceipt(e.target.value)}
              error={Boolean(problems.receiptNumber)}
              helperText={problems.receiptNumber}
              slotProps={{ htmlInput: { maxLength: 30 } }}
            />
          ) : null}
        </Box>
      </FormDialog>

      <FormDialog
        open={open === 'adhoc'}
        title="Add New Invoice"
        onClose={onClose}
        submitting={send.isPending}
        onSubmit={submit(() => {
          const found: Problems = {};
          if (!invoiceType) found.invoiceType = 'Select Invoice Type';
          const value = cents();
          if (Number.isNaN(value) || value === 0) found.amountCents = 'Amount is required';
          if (Object.keys(found).length) return setProblems(found);
          return run(
            'adhoc-invoices',
            { invoiceType, amountCents: value, includeTax, description },
            'Invoice added.',
          );
        })}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, mb: 2 }}>
          <Typography color="text.secondary">CustomerID:</Typography>
          <Typography>{customer.customerId}</Typography>
          <Typography color="text.secondary">Telephone Number:</Typography>
          <Typography>{customer.line.mdn ?? ''}</Typography>
          <Typography color="text.secondary">Customer Name:</Typography>
          <Typography>
            {customer.firstName} {customer.lastName}
          </Typography>
          <Typography color="text.secondary">Address:</Typography>
          <Typography>
            {customer.serviceAddress.line1}, {customer.serviceAddress.city}{' '}
            {customer.serviceAddress.state} {customer.serviceAddress.zip}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            select
            required
            label="Invoice Type:"
            value={invoiceType}
            onChange={(e) => setInvoiceType(e.target.value)}
            error={Boolean(problems.invoiceType)}
            helperText={problems.invoiceType}
          >
            {ADHOC_INVOICE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          {amountField('Amount ($):')}
          <TextField
            multiline
            minRows={2}
            label="Description Invoice:"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            helperText={`${100 - description.length} characters remaining`}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <Box>
            <FormLabel id="adhoc-tax">Do You want to include tax?:</FormLabel>
            <RadioGroup
              row
              aria-labelledby="adhoc-tax"
              value={includeTax ? 'Y' : 'N'}
              onChange={(e) => setIncludeTax(e.target.value === 'Y')}
            >
              <FormControlLabel value="Y" control={<Radio />} label="YES" />
              <FormControlLabel value="N" control={<Radio />} label="NO" />
            </RadioGroup>
          </Box>
        </Box>
      </FormDialog>

      <FormDialog
        open={open === 'credit'}
        title="Discount Credit"
        onClose={onClose}
        submitting={send.isPending}
        maxWidth="xs"
        onSubmit={submit(() => {
          const found: Problems = {};
          if (!creditOption) found.option = 'Select Discount Credit Option';
          const value = cents();
          if (Number.isNaN(value) || value === 0) found.amountCents = 'Amount is required';
          const count = Number(months);
          if (applicable === 'Recurring' && !(/^\d{1,2}$/.test(months) && count > 0))
            found.recurringMonths = 'Recurring Month Count is required';
          if (!typeId) found.typeId = 'Choose Discount Credit Type';
          if (Object.keys(found).length) return setProblems(found);
          return run(
            'discount-credit',
            {
              option: creditOption,
              applicableType: applicable,
              recurringMonths: applicable === 'Recurring' ? count : 0,
              amountCents: value,
              typeId,
            },
            applicable === 'Recurring'
              ? 'Recurring discount credit added.'
              : creditOption === 'Add Credit'
                ? 'Discount credit added.'
                : 'Discount credit deducted.',
          );
        })}
      >
        <WhoBlock />
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Current Available Discount Credit ( in $ )"
            value={(billing.discountCreditCents / 100).toFixed(2)}
            disabled
          />
          <TextField
            select
            required
            label="Discount Credit Option"
            value={creditOption}
            onChange={(e) => setCreditOption(e.target.value as DiscountOption)}
            error={Boolean(problems.option)}
            helperText={problems.option}
          >
            {DISCOUNT_CREDIT_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <FormLabel id="credit-applicable">Coupon Applicable Type</FormLabel>
            <RadioGroup
              row
              aria-labelledby="credit-applicable"
              value={applicable}
              onChange={(e) => setApplicable(e.target.value as DiscountApplicable)}
            >
              {DISCOUNT_APPLICABLE_TYPES.map((t) => (
                <FormControlLabel key={t} value={t} control={<Radio />} label={t} />
              ))}
            </RadioGroup>
          </Box>
          {applicable === 'Recurring' ? (
            <TextField
              required
              label="Recurring Month Count"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              error={Boolean(problems.recurringMonths)}
              helperText={problems.recurringMonths}
              slotProps={{ htmlInput: { maxLength: 2, inputMode: 'numeric' } }}
            />
          ) : null}
          <TextField
            required
            label="Amount ( in $ )"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={Boolean(problems.amountCents)}
            helperText={problems.amountCents}
            slotProps={{ htmlInput: { maxLength: 6, inputMode: 'decimal' } }}
          />
          <TextField
            select
            required
            label="Choose Discount Credit Type"
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            error={Boolean(problems.typeId || creditTypes.error)}
            helperText={
              problems.typeId ||
              (creditTypes.error ? 'The discount credit types could not be loaded.' : undefined) ||
              (creditTypes.data?.length === 0
                ? 'Add one under Manage Discount Credit Types first.'
                : undefined)
            }
          >
            {(creditTypes.data ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {String(t.values.name ?? '')}
              </MenuItem>
            ))}
          </TextField>
          {problems.option && creditOption ? (
            <Alert severity="error">{problems.option}</Alert>
          ) : null}
        </Box>
      </FormDialog>

      <FormDialog
        open={open === 'addWallet'}
        title="Add Wallet"
        onClose={onClose}
        submitting={send.isPending}
        maxWidth="xs"
        onSubmit={submit(async () => {
          if (!needAmount()) return;
          if (mode === 'card') {
            const card = await collectCard();
            if (!card) return;
            return run(
              'wallet',
              { amountCents: cents(), mode, card },
              'Money added to the wallet.',
            );
          }
          if (mode === 'cash' && !receipt.trim())
            return setProblems({ receiptNumber: 'Receipt Number is required' });
          return run(
            'wallet',
            { amountCents: cents(), mode, receiptNumber: receipt },
            'Money added to the wallet.',
          );
        })}
      >
        <WhoBlock />
        <Box sx={{ display: 'grid', gap: 2 }}>
          {amountField('Amount ( in $ )')}
          <TextField label="Processing Fee:" value="0.00" slotProps={READ_ONLY} />
          <TextField label="Total Amount:" value={(total / 100).toFixed(2)} slotProps={READ_ONLY} />
          <Box>
            <FormLabel id="wallet-mode">Payment Mode</FormLabel>
            <RadioGroup
              row
              aria-labelledby="wallet-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as 'cash' | 'skip' | 'card')}
            >
              <FormControlLabel value="cash" control={<Radio />} label="Cash" />
              {cardsAvailable && can(CARD_CODES.addWallet) ? (
                <FormControlLabel
                  value="card"
                  control={<Radio />}
                  label={`Credit Card (${CARD_CODES.addWallet})`}
                />
              ) : null}
              {can('PC99') ? (
                <FormControlLabel value="skip" control={<Radio />} label="Skip Payment (PC99)" />
              ) : null}
            </RadioGroup>
          </Box>
          {mode === 'card' ? (
            <CardField
              ref={cardRef}
              customerId={customer.id}
              error={problems.card ?? problems['card.savedCardId']}
            />
          ) : null}
          {mode === 'cash' ? (
            <TextField
              required
              label="Receipt Number"
              value={receipt}
              onChange={(e) => setReceipt(e.target.value)}
              error={Boolean(problems.receiptNumber)}
              helperText={problems.receiptNumber}
              slotProps={{ htmlInput: { maxLength: 30 } }}
            />
          ) : null}
        </Box>
      </FormDialog>

      <FormDialog
        open={open === 'adjustWallet'}
        title="CSR Screen For Adjust Wallet"
        onClose={onClose}
        submitting={send.isPending}
        maxWidth="xs"
        onSubmit={submit(() => {
          if (!needAmount(true)) return;
          if (!reason.trim()) return setProblems({ reason: 'Reason is required' });
          return run('wallet-adjust', { amountCents: cents(true), reason }, 'Wallet adjusted.');
        })}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, mb: 1 }}>
          <Typography color="text.secondary">Customer ID</Typography>
          <Typography>{customer.customerId}</Typography>
          <Typography color="text.secondary">Customer Name</Typography>
          <Typography>
            {customer.firstName} {customer.lastName}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Wallet Balance"
            value={(billing.walletBalanceCents / 100).toFixed(2)}
            disabled
          />
          {amountField('Adjust Amount', true)}
          <TextField
            required
            multiline
            minRows={2}
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            error={Boolean(problems.reason)}
            helperText={problems.reason}
            slotProps={{ htmlInput: { maxLength: 250 } }}
          />
        </Box>
      </FormDialog>

      <FormDialog
        open={open === 'ebill'}
        title="Ebill Setting"
        onClose={onClose}
        submitting={send.isPending}
        maxWidth="xs"
        onSubmit={submit(() => run('ebill', { eBill }, `E-Bill set to ${eBill}.`, true))}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, mb: 2 }}>
          <Typography color="text.secondary">Customer ID</Typography>
          <Typography>{customer.customerId}</Typography>
          <Typography color="text.secondary">Name</Typography>
          <Typography>
            {customer.firstName} {customer.lastName}
          </Typography>
          <Typography color="text.secondary">MDN</Typography>
          <Typography>{customer.line.mdn ?? ''}</Typography>
        </Box>
        <TextField
          select
          label="E-Bill"
          value={eBill}
          onChange={(e) => setEBill(e.target.value as EBillOption)}
        >
          {EBILL_OPTIONS.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </FormDialog>
    </>
  );
}

/* --------------------------------- The page -------------------------------- */

interface MenuEntry {
  label: string;
  /** The code shown after the label; the reference shows some without "PC". */
  code: string;
  /** The permission it needs when that is not `code`. */
  perm?: string;
  list?: ListKey;
  dialog?: DialogKey;
}

/** A menu button of the reference: a list to show or a dialog to open, each with its code. */
function MenuButton({
  label,
  entries,
  onPick,
  active,
}: {
  label: string;
  entries: MenuEntry[];
  onPick: (entry: MenuEntry) => void;
  active: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (entries.length === 0) return null;
  return (
    <>
      <Button
        variant={active ? 'contained' : 'outlined'}
        endIcon={<ExpandMoreIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {entries.map((e) => (
          <MenuItem
            key={e.label}
            onClick={() => {
              setAnchor(null);
              onPick(e);
            }}
          >
            {e.label} ({e.code})
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Billing And Invoice (PC75), laid out as the reference's page (product inventory, appendix
 * 13.6): the menu buttons Invoices, Payment, Wallet and More, the "BILLING SUMMARY" card, and
 * the list chosen from a menu ("Currently viewing"). Lists and dialogs show with their codes.
 */
export function BillingPage() {
  const { customer } = useCustomerContext();
  const { can: canDo } = useAuth();
  const featureOn = useFeatureReleased();
  // Sections added on 2026-10-21 each have a release switch; an off one is not offered.
  const FEATURE_OF: Record<string, string> = {
    PC932: 'billing-declined-payment',
    PC465: 'billing-ocs-purchase',
    PC678: 'billing-autopay-log',
    PC459: 'billing-tax-exemption',
  };
  const can = (code: string, list?: ListKey) => {
    const feature = list === 'applyCoupon' ? 'billing-apply-coupon' : FEATURE_OF[code];
    return canDo(code) && (!feature || featureOn(feature));
  };
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [list, setList] = useState<ListKey>('invoices');
  const [dialog, setDialog] = useState<DialogKey | null>(null);
  const [refunding, setRefunding] = useState<InvoiceDto | null>(null);
  const billing = useQuery({
    queryKey: billingKey(customer.id),
    queryFn: () => api.get<CustomerBillingDto>(`/billing/customers/${customer.id}`),
  });
  const voidOne = useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<CustomerBillingDto>(`/billing/customers/${customer.id}/invoices/${invoiceId}/void`),
    onSuccess: (next) => queryClient.setQueryData(billingKey(customer.id), next),
  });
  if (billing.error)
    return <ErrorState error={billing.error} onRetry={() => void billing.refetch()} />;
  if (!billing.data) return <LoadingState />;
  const b = billing.data;

  const menus: Array<{ label: string; entries: MenuEntry[] }> = [
    {
      label: 'Invoices',
      entries: [
        { label: 'Regular Invoices', code: 'PC72', list: 'invoices' as const },
        { label: 'Pay Invoice(s)', code: 'PC67', dialog: 'pay' as const },
        { label: 'Add Ad hoc Invoice', code: 'PC454', dialog: 'adhoc' as const },
      ].filter((e) => can(e.code)),
    },
    {
      label: 'Payment',
      entries: [
        { label: 'Payments', code: 'PC463', list: 'payments' as const },
        { label: 'Declined Payment', code: 'PC932', list: 'declined' as const },
      ].filter((e) => can(e.code)),
    },
    {
      label: 'Discount Credit',
      entries: [
        { label: 'Discount Credit Log', code: '843', perm: 'PC843', list: 'credit' as const },
        { label: 'Add Discount Credit', code: 'PC843', dialog: 'credit' as const },
      ].filter((e) => can(e.perm ?? e.code)),
    },
    {
      label: 'Wallet',
      entries: [
        { label: 'Wallet Log', code: 'PC466', list: 'wallet' as const },
        { label: 'Add Wallet', code: 'PC456', dialog: 'addWallet' as const },
        { label: 'Adjust Wallet', code: 'PC765', dialog: 'adjustWallet' as const },
      ].filter((e) => can(e.code)),
    },
    {
      label: 'More',
      entries: [
        { label: 'OCS Purchase', code: 'PC465', list: 'ocs' as const },
        { label: 'Autopay Log', code: 'PC678', list: 'autopay' as const },
        { label: 'Recurring Discount Log', code: '843', perm: 'PC843', list: 'recurring' as const },
        { label: 'Coupons log', code: 'PC1195', list: 'coupons' as const },
        { label: 'Apply Coupon', code: 'PC1195', list: 'applyCoupon' as const, perm: 'PC1195' },
        { label: 'E-Bill Setting', code: 'PC460', dialog: 'ebill' as const },
        { label: 'Tax Exemption Setting', code: 'PC459', dialog: 'tax' as const },
      ].filter((e) => can(e.perm ?? e.code, e.list)),
    },
  ];
  const listCode: Record<ListKey, string> = {
    invoices: 'PC72',
    payments: 'PC463',
    credit: 'PC843',
    wallet: 'PC466',
    recurring: 'PC843',
    coupons: 'PC1195',
    declined: 'PC932',
    ocs: 'PC465',
    autopay: 'PC678',
    applyCoupon: 'PC1195',
  };
  const shownList = can(listCode[list], list)
    ? list
    : (Object.keys(listCode) as ListKey[]).find((k) => can(listCode[k], k));
  const current = b.summary.current;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography>
          Wallet Balance: <strong>{formatCents(b.walletBalanceCents)}</strong>
        </Typography>
        <Typography>
          Discount Credit: <strong>{formatCents(b.discountCreditCents)}</strong>
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }} aria-label="Billing menu">
        {menus.map((m) => (
          <MenuButton
            key={m.label}
            label={m.label}
            entries={m.entries}
            active={m.entries.some((e) => e.list === shownList)}
            onPick={(e) => (e.list ? setList(e.list) : setDialog(e.dialog ?? null))}
          />
        ))}
      </Box>

      <Section title="BILLING SUMMARY">
        <Box
          aria-label="Billing summary"
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}
        >
          <Box>
            <Typography variant="overline">CURRENT BILL</Typography>
            {current ? (
              <>
                <Typography variant="h3" component="div">
                  {formatCents(current.amountCents)}{' '}
                  <Chip
                    size="small"
                    label={current.status === 'Paid' ? 'Current Paid' : 'Current Unpaid'}
                    color={current.status === 'Paid' ? 'success' : 'warning'}
                  />
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  Invoice: {current.invoiceNo} · Due: {formatDate(current.dueDate)}
                  {current.paidAt
                    ? ` · Paid on: ${formatDate(current.paidAt)} · Method: ${current.method}`
                    : ''}
                </Typography>
              </>
            ) : (
              <Typography>No invoice yet.</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="overline">BILL BREAKDOWN</Typography>
            <Typography variant="h3" component="p">
              {formatCents(b.summary.netDueCents)}{' '}
              <Typography component="span" sx={{ fontSize: 13 }}>
                net due
              </Typography>
            </Typography>
            {current ? (
              <Typography sx={{ fontSize: 13 }}>
                CHARGES: Gross {formatCents(current.amountCents)} · Debit{' '}
                {formatCents(current.debitCents)}
              </Typography>
            ) : null}
          </Box>
          <Box>
            <Typography variant="overline">PLAN</Typography>
            {b.summary.plan ? (
              <Typography>
                {b.summary.plan.name} <strong>{formatCents(b.summary.plan.priceCents)}</strong> /mo
              </Typography>
            ) : (
              <Typography>No plan.</Typography>
            )}
          </Box>
        </Box>
      </Section>

      {shownList ? (
        <Section title={LIST_TITLES[shownList]}>
          <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
            Currently viewing
          </Typography>
          {shownList === 'invoices' ? (
            <InvoiceList
              invoices={b.invoices}
              onRefund={featureOn('payment-refunds') && can('PC692') ? setRefunding : undefined}
              canVoid={can('PC609')}
              busy={voidOne.isPending}
              onVoid={(i) =>
                voidOne.mutate(i.id, {
                  onSuccess: () => notify.success(`Invoice ${i.invoiceNo} voided.`),
                  onError: (err) => notify.error(err),
                })
              }
            />
          ) : null}
          {shownList === 'payments' ? <PaymentList payments={b.payments} /> : null}
          {shownList === 'credit' ? (
            <DataTable
              label="Discount Credit Log"
              columns={CREDIT_COLUMNS}
              rows={b.discountCreditLog}
              rowKey={(t) => t.id}
              emptyText="No record found"
            />
          ) : null}
          {shownList === 'recurring' ? (
            <DataTable
              label="Recurring Discount Log"
              columns={RECURRING_COLUMNS}
              rows={b.recurringDiscounts}
              rowKey={(d) => d.id}
              emptyText="No record found"
            />
          ) : null}
          {shownList === 'wallet' ? (
            <>
              <Typography sx={{ mb: 1 }}>
                Total Wallet: <strong>{formatCents(b.walletBalanceCents)}</strong>
              </Typography>
              <DataTable
                label="Wallet Log"
                columns={WALLET_COLUMNS}
                rows={b.walletLog}
                rowKey={(t) => t.id}
                emptyText="No record found"
              />
            </>
          ) : null}
          {shownList === 'coupons' ? <CouponsLog /> : null}
          {shownList === 'declined' ? <DeclinedPaymentList customerId={customer.id} /> : null}
          {shownList === 'ocs' ? <OcsPurchaseList customerId={customer.id} /> : null}
          {shownList === 'autopay' ? <AutopayLogList customerId={customer.id} /> : null}
          {shownList === 'applyCoupon' ? (
            <ApplyCouponPanel customerId={customer.id} billing={b} />
          ) : null}
        </Section>
      ) : null}

      <BillingDialogs open={dialog} billing={b} onClose={() => setDialog(null)} />
      {refunding ? (
        <RefundDialog
          customerId={customer.id}
          invoice={refunding}
          onClose={() => setRefunding(null)}
        />
      ) : null}
      <TaxExemptionDialog
        customerId={customer.id}
        open={dialog === 'tax'}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

/* ------------------------------ Refund (2026-10-29) ------------------------- */

/**
 * "Refund" on a paid invoice (PC692, switch payment-refunds): Full gives back what is left of what
 * was paid, Partial an amount up to that. 212Ez records it; the money is handed back outside.
 */
function RefundDialog({
  customerId,
  invoice,
  onClose,
}: {
  customerId: string;
  invoice: InvoiceDto;
  onClose: () => void;
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const left = invoice.amountPaidCents - invoice.refundedCents;
  const [refundType, setRefundType] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [problems, setProblems] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (body: RefundInput) =>
      api.post<CustomerBillingDto>(
        `/billing/customers/${customerId}/invoices/${invoice.id}/refund`,
        body,
      ),
    onSuccess: (next) => queryClient.setQueryData(billingKey(customerId), next),
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = {
      refundType,
      amountCents:
        refundType === 'Partial' && amount.trim() !== '' && !Number.isNaN(Number(amount))
          ? Math.round(Number(amount) * 100)
          : null,
      reason,
    };
    const parsed = refundSchema.safeParse(body);
    if (!parsed.success) {
      setProblems(
        Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])),
      );
      return;
    }
    try {
      await save.mutateAsync(parsed.data);
      notify.success(`Refund recorded on invoice ${invoice.invoiceNo}.`);
      onClose();
    } catch (err) {
      const found = Object.fromEntries(
        ['refundType', 'amountCents', 'reason']
          .map((f) => [
            f,
            err instanceof ApiError ? (err.details.find((d) => d.path === f)?.message ?? '') : '',
          ])
          .filter(([, m]) => m),
      );
      setProblems(found);
      if (Object.keys(found).length === 0) notify.error(err);
    }
  };
  return (
    <FormDialog
      open
      title={`Refund invoice ${invoice.invoiceNo}`}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      submitLabel="Refund"
      cancelLabel="Close"
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <Typography>
          Paid {formatCents(invoice.amountPaidCents)} by {invoice.paidMethod}; left to refund{' '}
          {formatCents(left)}.
        </Typography>
        <TextField
          select
          required
          label="Refund Type"
          value={refundType}
          onChange={(e) => setRefundType(e.target.value)}
          error={!!problems.refundType}
          helperText={problems.refundType}
        >
          {REFUND_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        {refundType === 'Partial' ? (
          <TextField
            required
            label="Refund Amount ($)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!problems.amountCents}
            helperText={problems.amountCents}
            slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 9 } }}
          />
        ) : null}
        <TextField
          required
          label="Refund Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={!!problems.reason}
          helperText={problems.reason}
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <Alert severity="info">
          212Ez records the refund; hand the money back the way it was paid.
        </Alert>
      </Box>
    </FormDialog>
  );
}

/* ------------------------------ Purchase History --------------------------- */

const PURCHASE_COLUMNS: Column<InvoiceDto>[] = [
  { key: 'date', header: 'Purchase Date', nowrap: true, render: (i) => formatDateTime(i.date) },
  { key: 'type', header: 'Purchase', render: (i) => i.invoiceType },
  { key: 'description', header: 'Description', render: (i) => i.description },
  { key: 'total', header: 'Amount', align: 'right', render: (i) => formatCents(i.balanceCents) },
  { key: 'paymentType', header: 'Payment Type', render: (i) => i.paidMethod },
  { key: 'invoiceNo', header: 'Invoice No', nowrap: true, render: (i) => i.invoiceNo },
  { key: 'postedBy', header: 'Purchased By', render: (i) => userLabel(i.createdBy) },
];

/**
 * Purchase History (PC58): everything bought for this line. The reference lists minute and
 * data reloads here; this list holds what was paid for (plans, top-ups, equipment, wallet).
 */
export function PurchaseHistoryPage() {
  const { customer } = useCustomerContext();
  const featureOn = useFeatureReleased();
  const purchases = useQuery({
    queryKey: ['billing', 'purchases', customer.id],
    queryFn: () => api.get<{ data: InvoiceDto[] }>(`/billing/customers/${customer.id}/purchases`),
    select: (r) => r.data,
  });
  return (
    <Section
      title="Purchases"
      actions={
        featureOn('purchase-reload') ? (
          // "Reload Purchases": reads the list again.
          <Button
            size="small"
            disabled={purchases.isFetching}
            onClick={() => void purchases.refetch()}
          >
            Reload Purchases
          </Button>
        ) : undefined
      }
    >
      <DataTable
        label="Purchases"
        columns={PURCHASE_COLUMNS}
        rows={purchases.data}
        rowKey={(i) => i.id}
        loading={purchases.isFetching}
        error={purchases.error}
        onRetry={() => void purchases.refetch()}
        emptyText="This customer has no purchases."
      />
    </Section>
  );
}
